import { Whisper } from '../AndroidPlugins';

// Ключ для хранения метрик в AsyncStorage
const WHISPER_METRICS_KEY = '@whisper_metrics';
const MAX_PROCESSING_RECORDS = 20;

// Структура записи метрики
// { processingTime: number, duration: number, timestamp: number, isCorrect: boolean }
let metricsCache = null;

// Загружаем метрики из localStorage при первом запуске
async function loadMetrics() {
  if (metricsCache !== null) return metricsCache;
  
  try {
    const stored = localStorage.getItem(WHISPER_METRICS_KEY);
    metricsCache = stored ? JSON.parse(stored) : [];
    return metricsCache;
  } catch (error) {
    console.warn('[Whisper] Failed to load metrics from localStorage:', error);
    metricsCache = [];
    return metricsCache;
  }
}

// Сохраняем метрики в localStorage
async function saveMetrics(metrics) {
  try {
    localStorage.setItem(WHISPER_METRICS_KEY, JSON.stringify(metrics));
    metricsCache = metrics;
  } catch (error) {
    console.warn('[Whisper] Failed to save metrics to localStorage:', error);
  }
}

// Добавляем новую метрику
async function addMetric(processingTime, duration, isCorrect = null) {
  const metrics = await loadMetrics();
  
  const newMetric = {
    processingTime,
    duration,
    timestamp: Date.now(),
    isCorrect // true - правильно, false - неправильно, null - неизвестно
  };
  
  metrics.push(newMetric);
  
  // Оставляем только последние записи
  if (metrics.length > MAX_PROCESSING_RECORDS) {
    metrics.splice(0, metrics.length - MAX_PROCESSING_RECORDS);
  }
  
  await saveMetrics(metrics);
  return metrics;
}

// Рассчитываем средние показатели
function calculateAverages(metrics) {
  if (metrics.length === 0) {
    return {
      averageProcessingTime: 0,
      averageDuration: 0,
      avgMsPerSecond: 0,
      correctRecognitions: 0,
      totalRecognitions: 0,
      accuracyRate: 0
    };
  }
  
  const totalProcessingTime = metrics.reduce((sum, m) => sum + m.processingTime, 0);
  const totalDuration = metrics.reduce((sum, m) => sum + m.duration, 0);
  
  const averageProcessingTime = totalProcessingTime / metrics.length;
  const averageDuration = totalDuration / metrics.length;
  const avgMsPerSecond = totalDuration > 0 ? totalProcessingTime / totalDuration : 0;
  
  // Считаем точность распознавания
  const recognitionsWithResults = metrics.filter(m => m.isCorrect !== null);
  const correctRecognitions = metrics.filter(m => m.isCorrect === true).length;
  const totalRecognitions = recognitionsWithResults.length;
  const accuracyRate = totalRecognitions > 0 ? (correctRecognitions / totalRecognitions) * 100 : 0;
  
  return {
    averageProcessingTime,
    averageDuration,
    avgMsPerSecond,
    correctRecognitions,
    totalRecognitions,
    accuracyRate
  };
}

// Функция для обновления результата последнего распознавания
export async function updateLastRecognitionResult(isCorrect) {
  try {
    const metrics = await loadMetrics();
    if (metrics.length === 0) {
      console.warn('[Whisper] No metrics found to update');
      return;
    }
    
    // Обновляем последнюю запись
    const lastMetric = metrics[metrics.length - 1];
    lastMetric.isCorrect = isCorrect;
    
    await saveMetrics(metrics);
    
    const averages = calculateAverages(metrics);
    return averages;
  } catch (error) {
    console.error('[Whisper] Failed to update recognition result:', error);
  }
}

// Функция для получения текущих метрик (для внешнего использования)
export async function getWhisperMetrics() {
  const metrics = await loadMetrics();
  const averages = calculateAverages(metrics);
  
  return {
    recordsCount: metrics.length,
    metrics: metrics.slice(), // копия массива
    ...averages
  };
}


function buildInitPromptFromOptions(options) {
  return options
    .map(opt => opt.userText?.trim())
    .filter(Boolean)
    .join('\n');
}

export async function runWhisper(audioData, duration, language = 'auto', options = [], isCorrect = null) {
  try {
    // Проверяем, что audioData валиден
    if (
      !audioData ||
      (typeof audioData.length !== 'number') ||
      audioData.length === 0 ||
      typeof audioData[0] !== 'number'
    ) {
      throw new Error('Invalid or empty audio data');
    }
    if (typeof duration !== 'number' || duration <= 0) {
      throw new Error('Invalid duration value');
    }
    
    const initPrompt = buildInitPromptFromOptions(options);
    
    // Замеряем время начала
    const startTime = performance.now();
    
    const result = await Whisper.transcribe({
      audioData: Array.from(audioData),
      language,
      initPrompt
    });
    
    // Замеряем время окончания и вычисляем длительность
    const endTime = performance.now();
    const processingTime = endTime - startTime;
    
    // Сохраняем метрику и получаем обновлённый список
    const updatedMetrics = await addMetric(processingTime, duration, isCorrect);
    
    // Рассчитываем текущие и средние показатели
    const currentMsPerSecond = processingTime / duration;
    const averages = calculateAverages(updatedMetrics);
    
    // console.log('[Whisper] Transcription time:', processingTime.toFixed(2), 'ms');
    // console.log('[Whisper] Current audio duration:', duration.toFixed(2), 's');
    // console.log('[Whisper] Current speed:', currentMsPerSecond.toFixed(2), 'ms per second of audio');
    // console.log('[Whisper] Average processing time (last', updatedMetrics.length, 'records):', averages.averageProcessingTime.toFixed(2), 'ms');
    // console.log('[Whisper] Average audio duration:', averages.averageDuration.toFixed(2), 's');
    // console.log('[Whisper] Average speed:', averages.avgMsPerSecond.toFixed(2), 'ms per second of audio');
    
    return {
      text: result.text?.trim() || '',
      processingTime,                    // Время текущей обработки (мс)
      duration,                          // Длительность текущего аудио (сек)
      currentMsPerSecond,               // Скорость для текущего вызова
      averageProcessingTime: averages.averageProcessingTime,  // Среднее время обработки
      averageDuration: averages.averageDuration,              // Средняя длительность аудио
      avgMsPerSecond: averages.avgMsPerSecond,               // Правильная средняя скорость
      metricsCount: updatedMetrics.length,                   // Количество записей в статистике
      correctRecognitions: averages.correctRecognitions,     // Количество правильных распознаваний
      totalRecognitions: averages.totalRecognitions,         // Общее количество распознаваний с результатом
      accuracyRate: averages.accuracyRate                    // Процент точности
    };
  } catch (error) {
    console.error('[Whisper] Error during transcription:', error);
    throw error;
  }
}
/*
import { Whisper } from '../AndroidPlugins';

// Ключ для хранения метрик в AsyncStorage
const WHISPER_METRICS_KEY = '@whisper_metrics';
const MAX_PROCESSING_RECORDS = 100;

// Структура записи метрики
// { processingTime: number, duration: number, timestamp: number }
let metricsCache = null;

// Загружаем метрики из localStorage при первом запуске
async function loadMetrics() {
  if (metricsCache !== null) return metricsCache;
  
  try {
    const stored = localStorage.getItem(WHISPER_METRICS_KEY);
    metricsCache = stored ? JSON.parse(stored) : [];
    return metricsCache;
  } catch (error) {
    console.warn('[Whisper] Failed to load metrics from localStorage:', error);
    metricsCache = [];
    return metricsCache;
  }
}

// Сохраняем метрики в localStorage
async function saveMetrics(metrics) {
  try {
    localStorage.setItem(WHISPER_METRICS_KEY, JSON.stringify(metrics));
    metricsCache = metrics;
  } catch (error) {
    console.warn('[Whisper] Failed to save metrics to localStorage:', error);
  }
}

// Добавляем новую метрику
async function addMetric(processingTime, duration) {
  const metrics = await loadMetrics();
  
  const newMetric = {
    processingTime,
    duration,
    timestamp: Date.now()
  };
  
  metrics.push(newMetric);
  
  // Оставляем только последние записи
  if (metrics.length > MAX_PROCESSING_RECORDS) {
    metrics.splice(0, metrics.length - MAX_PROCESSING_RECORDS);
  }
  
  await saveMetrics(metrics);
  return metrics;
}

// Рассчитываем средние показатели
function calculateAverages(metrics) {
  if (metrics.length === 0) {
    return {
      averageProcessingTime: 0,
      averageDuration: 0,
      avgMsPerSecond: 0
    };
  }
  
  const totalProcessingTime = metrics.reduce((sum, m) => sum + m.processingTime, 0);
  const totalDuration = metrics.reduce((sum, m) => sum + m.duration, 0);
  
  const averageProcessingTime = totalProcessingTime / metrics.length;
  const averageDuration = totalDuration / metrics.length;
  const avgMsPerSecond = totalDuration > 0 ? totalProcessingTime / totalDuration : 0;
  
  return {
    averageProcessingTime,
    averageDuration,
    avgMsPerSecond
  };
}

// Функция для получения текущих метрик (для внешнего использования)
export async function getWhisperMetrics() {
  const metrics = await loadMetrics();
  const averages = calculateAverages(metrics);
  
  return {
    recordsCount: metrics.length,
    metrics: metrics.slice(), // копия массива
    ...averages
  };
}

function buildInitPromptFromOptions(options) {
  return options
    .map(opt => opt.userText?.trim())
    .filter(Boolean)
    .join('\n');
}

export async function runWhisper(audioData, duration, language = 'auto', options = []) {
  try {
    // Проверяем, что audioData валиден
    if (
      !audioData ||
      (typeof audioData.length !== 'number') ||
      audioData.length === 0 ||
      typeof audioData[0] !== 'number'
    ) {
      throw new Error('Invalid or empty audio data');
    }
    if (typeof duration !== 'number' || duration <= 0) {
      throw new Error('Invalid duration value');
    }
    
    const initPrompt = buildInitPromptFromOptions(options);
    
    // Замеряем время начала
    const startTime = performance.now();
    
    const result = await Whisper.transcribe({
      audioData: Array.from(audioData),
      language,
      initPrompt
    });
    
    // Замеряем время окончания и вычисляем длительность
    const endTime = performance.now();
    const processingTime = endTime - startTime;
    
    // Сохраняем метрику и получаем обновлённый список
    const updatedMetrics = await addMetric(processingTime, duration);
    
    // Рассчитываем текущие и средние показатели
    const currentMsPerSecond = processingTime / duration;
    const averages = calculateAverages(updatedMetrics);
    
    // console.log('[Whisper] Transcription time:', processingTime.toFixed(2), 'ms');
    // console.log('[Whisper] Current audio duration:', duration.toFixed(2), 's');
    // console.log('[Whisper] Current speed:', currentMsPerSecond.toFixed(2), 'ms per second of audio');
    // console.log('[Whisper] Average processing time (last', updatedMetrics.length, 'records):', averages.averageProcessingTime.toFixed(2), 'ms');
    // console.log('[Whisper] Average audio duration:', averages.averageDuration.toFixed(2), 's');
    // console.log('[Whisper] Average speed:', averages.avgMsPerSecond.toFixed(2), 'ms per second of audio');
       
    return {
      text: result.text?.trim() || '',
      processingTime,                    // Время текущей обработки (мс)
      duration,                          // Длительность текущего аудио (сек)
      currentMsPerSecond,               // Скорость для текущего вызова
      averageProcessingTime: averages.averageProcessingTime,  // Среднее время обработки
      averageDuration: averages.averageDuration,              // Средняя длительность аудио
      avgMsPerSecond: averages.avgMsPerSecond,               // Правильная средняя скорость
      metricsCount: updatedMetrics.length,                   // Количество записей в статистике
    };
  } catch (error) {
    console.error('[Whisper] Error during transcription:', error);
    throw error;
  }
}
*/